import type { FC } from 'react';
import { generatePath } from 'react-router-dom';
import type { AuditEntry } from '@domain/types/audit-log';
import Nav from '@src/routes';
import clsx from 'clsx';
import { ExternalLink } from 'lucide-react';

import { LINK } from './primitives';

const ENDORSEMENT_BATCH_CLONE_STATUS: Record<string, { label: string; className: string }> = {
  SUCCEEDED: { label: 'Complete', className: 'bg-success-100 text-success-800' },
  FAILED: { label: 'Failed', className: 'bg-error-100 text-error-800' },
  RUNNING: { label: 'Processing', className: 'bg-info-100 text-info-800' },
};

const CtaLink: FC<{ href: string; label: string; title: string; className?: string }> = ({
  href,
  label,
  title,
  className,
}) => (
  <a
    href={href}
    className={clsx(LINK, 'inline-flex w-fit items-center gap-1.5 text-xs leading-5', className)}
    target="_blank"
    rel="noreferrer"
    title={title}
  >
    <ExternalLink className="h-3.5 w-3.5 shrink-0" />
    {label}
  </a>
);

export const EntryCta: FC<{ entry: AuditEntry }> = ({ entry }) => {
  if (entry.action === 'renewal.created') {
    return (
      <CtaLink
        href={generatePath(Nav.SubmissionContract, { id: entry.child.id })}
        label="Open Contract"
        title={entry.child.name}
        className="mt-2"
      />
    );
  }

  if (entry.action === 'endorsement.duplicated') {
    const status = ENDORSEMENT_BATCH_CLONE_STATUS[entry.status] ?? ENDORSEMENT_BATCH_CLONE_STATUS.RUNNING;
    const { id, name } = entry.targetEntity;
    return (
      <div className="mt-2 flex items-center gap-4">
        <span className={clsx('rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap', status.className)}>
          {status.label}
        </span>
        <CtaLink href={generatePath(Nav.SubmissionContract, { id })} label="Open Contract" title={name ?? id} />
      </div>
    );
  }

  if (entry.action === 'endorsement.duplicated_from') {
    return (
      <CtaLink
        href={generatePath(Nav.SubmissionContract, { id: entry.sourceContract.id })}
        label="Open Contract"
        title={entry.sourceContract.name}
        className="mt-2"
      />
    );
  }

  if (entry.action === 'endorsement.merged') {
    return (
      <CtaLink
        href={generatePath(Nav.EndorsementView, {
          id: entry.parentSubmissionId,
          endorsement_id: entry.endorsement.id,
        })}
        label="Open Endorsement"
        title={entry.endorsement.name}
        className="mt-2"
      />
    );
  }

  return null;
};
